// auth_ui.ts — Gating d'authentification côté front.
//
// Migration S2.2 du plan TS+Vite (2026-05-10) : auth_ui.js → auth_ui.ts strict.
//
// Chargé après api.ts. Comportement :
// 1. Au démarrage, on tente GET /api/v1/auth/me.
//    - Si 200 : on stocke l'user, on ajoute un bouton "Déconnexion" dans le header.
//    - Si 401 : on affiche un overlay de login plein écran (bloque toute l'UI).
// 2. Lors d'un 401 ultérieur (session expirée), api.ts dispatche
//    l'événement window "auth:expired" que l'on écoute pour rouvrir l'overlay.

// __authUser : type déjà déclaré dans app/static/js/types/window.d.ts
// (forme {id, trigram, name, role} | null). À fusionner ici quand
// window.d.ts sera retiré (S4-S5, après migration de tous les modules).
type AuthUser = NonNullable<Window['__authUser']>;

declare global {
  interface WindowEventMap {
    'auth:ready': Event;
  }
}

(function () {
  'use strict';

  window.__authUser = null;

  // ── h() : helper micro-DOM, équivalent React.createElement light ────────
  type AttrValue = string | number | boolean | EventListener | null | undefined;
  type Child = string | Node | null | undefined;

  function h<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attrs: Record<string, AttrValue> = {},
    children: Child | Child[] = [],
  ): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null) continue;
      if (k === 'style') {
        el.style.cssText = String(v);
      } else if (k.startsWith('on') && typeof v === 'function') {
        el.addEventListener(k.slice(2), v as EventListener);
      } else {
        el.setAttribute(k, String(v));
      }
    }
    const list: Child[] = Array.isArray(children) ? children : [children];
    for (const c of list) {
      if (c == null) continue;
      el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return el;
  }

  // ── Overlay de login plein écran ────────────────────────────────────────

  function ensureOverlay(): HTMLElement {
    const existing = document.getElementById('auth-overlay');
    if (existing) {
      existing.style.display = 'flex';
      return existing;
    }

    const err = h('div', {
      id: 'auth-error',
      style: 'color:#b00020;font-size:13px;min-height:18px;',
    });

    const trigramInput = h('input', {
      id: 'auth-trigram',
      required: 'true',
      autocomplete: 'username',
      maxlength: '10',
      style: 'width:100%;padding:8px;box-sizing:border-box;text-transform:uppercase;',
    });

    const pwdInput = h('input', {
      id: 'auth-password',
      type: 'password',
      required: 'true',
      autocomplete: 'current-password',
      style: 'width:100%;padding:8px;box-sizing:border-box;',
    });

    const ov: HTMLDivElement = h('div', {
      id: 'auth-overlay',
      style:
        'position:fixed;inset:0;background:rgba(245,247,250,.98);z-index:99999;display:flex;align-items:center;justify-content:center;',
    });

    const form = h(
      'form',
      {
        id: 'auth-form',
        style:
          'width:320px;display:flex;flex-direction:column;gap:12px;padding:32px;background:#fff;border-radius:10px;box-shadow:0 10px 40px rgba(0,0,0,.2);font-family:system-ui,-apple-system,sans-serif;',
        onsubmit: (e: Event) => {
          e.preventDefault();
          err.textContent = '';
          const trigram = trigramInput.value.trim().toUpperCase();
          const password = pwdInput.value;
          // IIFE async pour préserver la signature `(e) => void` attendue
          // par EventListener (un onsubmit qui retourne Promise crée un
          // unhandled rejection si le navigateur ne l'await pas).
          void (async () => {
            try {
              const user = (await window.api.login({ trigram, password })) as AuthUser;
              window.__authUser = user;
              ov.style.display = 'none';
              // Nouvelle connexion : on réaffiche le bandeau "Mode restreint"
              // (purge du flag de fermeture posé par permissions_ui.ts).
              try {
                sessionStorage.removeItem('ornexia.readonlyBannerDismissed');
              } catch {
                /* sessionStorage indispo : sans effet, le bandeau restera visible. */
              }
              // Rechargement simple : évite d'avoir à ré-initialiser tout l'état de l'app.
              window.location.reload();
            } catch (ex: unknown) {
              const msg = ex instanceof Error ? ex.message : 'Identifiants invalides';
              err.textContent = msg;
            }
          })();
        },
      },
      [
        h('h2', { style: 'margin:0 0 4px 0;font-size:20px;' }, 'Connexion'),
        h('label', { style: 'display:flex;flex-direction:column;gap:4px;font-size:13px;' }, [
          'Trigramme',
          trigramInput,
        ]),
        h('label', { style: 'display:flex;flex-direction:column;gap:4px;font-size:13px;' }, [
          'Mot de passe',
          pwdInput,
        ]),
        h(
          'button',
          {
            type: 'submit',
            style:
              'padding:10px;background:#1D9E75;color:#fff;border:0;border-radius:6px;cursor:pointer;font-weight:600;',
          },
          'Se connecter',
        ),
        h(
          'a',
          {
            href: '/forgot.html',
            style: 'font-size:13px;color:#1D9E75;text-decoration:none;text-align:center;',
          },
          'Mot de passe oublié ?',
        ),
        err,
      ],
    );

    ov.appendChild(form);

    document.body.appendChild(ov);
    setTimeout(() => trigramInput.focus(), 50);
    return ov;
  }

  function hideOverlay(): void {
    const ov = document.getElementById('auth-overlay');
    if (ov) ov.style.display = 'none';
  }

  function injectHeader(user: AuthUser | null): void {
    // Le header ne contient plus que `#header-account-btn` (icône compte).
    // L'identité utilisateur (nom, trigramme, équipes) est exposée dans la
    // modale compte ouverte au clic sur l'icône (cf. modules/header.js).
    // On garde l'attribut title sur le bouton pour que le tooltip natif
    // affiche le nom au survol — pratique pour identifier rapidement
    // l'utilisateur connecté sans cliquer.
    const btn = document.getElementById('header-account-btn');
    if (btn && user) {
      btn.title = user.name ? `${user.name} — Mon compte` : 'Mon compte';
    }
  }

  async function bootstrap(): Promise<void> {
    try {
      window.__authUser = (await window.api.me()) as AuthUser;
      hideOverlay();
      // L'UI peut ne pas être prête à l'instant du check ; on retente au DOMContentLoaded.
      // `?? null` parce que window.__authUser est typé optionnel dans
      // window.d.ts (T | null | undefined) — exactOptionalPropertyTypes
      // refuse d'assigner undefined à un paramètre `T | null`.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () =>
          injectHeader(window.__authUser ?? null),
        );
      } else {
        injectHeader(window.__authUser ?? null);
      }
      // Notifie le reste de l'app que les perms sont chargées.
      try {
        window.dispatchEvent(new Event('auth:ready'));
      } catch (e) {
        // Pareil que api.ts : log mais pas bloquant.
        console.warn('[auth_ui] dispatchEvent auth:ready', e);
      }
    } catch {
      ensureOverlay();
    }
  }

  window.addEventListener('auth:expired', () => {
    window.__authUser = null;
    ensureOverlay();
  });

  // Démarre immédiatement (ne bloque pas le rendu du DOM, mais affichera
  // l'overlay par-dessus si nécessaire).
  void bootstrap();
})();

// Marker module ESM (pas d'export, pas d'import : module à effet de bord pur).
export {};
